/**
 * Invoice Notes API Client
 * Handles all API requests for invoice notes feature
 */

import axios from '../AxiosInstance';
import { getApiUrl } from '../../utils/ApiUtils';

/**
 * Invoice Notes API
 */
export const invoiceNotesApi = {
  /**
   * Get all notes for an invoice
   * @param {string} companyId - Company ID
   * @param {string} invoiceId - Invoice ID
   * @param {boolean} includeDeleted - Include deleted notes
   * @returns {Promise} API response
   */
  getAll: (companyId, invoiceId, includeDeleted = false) => {
    const url = getApiUrl(`${companyId}/invoices/${invoiceId}/notes`);
    return axios.get(url, {
      params: { include_deleted: includeDeleted },
      withCredentials: true
    });
  },

  /**
   * Get single note by ID
   * @param {string} companyId - Company ID
   * @param {string} invoiceId - Invoice ID
   * @param {string} noteId - Note ID
   * @returns {Promise} API response
   */
  getOne: (companyId, invoiceId, noteId) => {
    const url = getApiUrl(`${companyId}/invoices/${invoiceId}/notes/${noteId}`);
    return axios.get(url, { withCredentials: true });
  },

  /**
   * Create new note
   * @param {string} companyId - Company ID
   * @param {string} invoiceId - Invoice ID
   * @param {object} noteData - Note data
   * @returns {Promise} API response
   */
  create: (companyId, invoiceId, noteData) => {
    const url = getApiUrl(`${companyId}/invoices/${invoiceId}/notes`);
    return axios.post(url, noteData, { withCredentials: true });
  },

  /**
   * Update existing note
   * @param {string} companyId - Company ID
   * @param {string} invoiceId - Invoice ID
   * @param {string} noteId - Note ID
   * @param {object} noteData - Updated note data
   * @returns {Promise} API response
   */
  update: (companyId, invoiceId, noteId, noteData) => {
    const url = getApiUrl(`${companyId}/invoices/${invoiceId}/notes/${noteId}`);
    return axios.put(url, noteData, { withCredentials: true });
  },

  /**
   * Delete note (soft delete)
   * @param {string} companyId - Company ID
   * @param {string} invoiceId - Invoice ID
   * @param {string} noteId - Note ID
   * @returns {Promise} API response
   */
  delete: (companyId, invoiceId, noteId) => {
    const url = getApiUrl(`${companyId}/invoices/${invoiceId}/notes/${noteId}`);
    return axios.delete(url, { withCredentials: true });
  },

  /**
   * Toggle pin status
   * @param {string} companyId - Company ID
   * @param {string} invoiceId - Invoice ID
   * @param {string} noteId - Note ID
   * @returns {Promise} API response
   */
  togglePin: (companyId, invoiceId, noteId) => {
    const url = getApiUrl(`${companyId}/invoices/${invoiceId}/notes/${noteId}/pin`);
    return axios.put(url, {}, { withCredentials: true });
  }
};

export default invoiceNotesApi;
